import { z } from 'zod';

/**
 * Enum for valid chat command types
 */
export const ChatCommandTypeEnum = z.enum(['fixCompilationError', 'rollback', 'chatDecision']);

/**
 * Schema for fixCompilationError command parameters
 */
export const FixCompilationErrorParams = z.object({
  errorMessageId: z.string().describe('ID of the error message to fix'),
});

/**
 * Schema for rollback command parameters
 *
 * @description rollback to a checkpoint. 3 different scenarios are possible:
 * 1. rollback to a checkpoint that is not the current checkpoint - just provide checkpointId param
 * 2. retry rollback - provide both checkpointId (targetCheckpointId) and originalCheckpointId params from 'rollback' error
 * 3. abort rollback - swap checkpointId and originalCheckpointId values:
 *    set originalCheckpointId to the value of targetCheckpointId from rollback error
 *    and set checkpointId to the value of originalCheckpointId from rollback error
 *
 * @param checkpointId - ID of the checkpoint to rollback to
 * @param originalCheckpointId - ID of the original checkpoint, if not provided, the current checkpoint will be used, will be used during rollback retry
 */
export const RollbackParams = z.object({
  checkpointId: z.string().describe('ID of the checkpoint to rollback to'),
  originalCheckpointId: z
    .string()
    .optional()
    .describe(
      'ID of the original checkpoint, if not provided, the current checkpoint will be used, will be used during rollback retry'
    ),
});

export const ChatDecisionParams = z.object({
  decision: z.string().describe('Decision made by user'),
});

/**
 * Schema for chat command request using discriminated union
 */
export const ChatCommandSchema = z.discriminatedUnion('command', [
  z.object({
    command: z.literal(ChatCommandTypeEnum.enum.fixCompilationError),
    args: FixCompilationErrorParams.describe('Parameters for fixing compilation error'),
  }),
  z.object({
    command: z.literal(ChatCommandTypeEnum.enum.rollback),
    args: RollbackParams.describe('Parameters for rolling back to a checkpoint'),
  }),
  z.object({
    command: z.literal(ChatCommandTypeEnum.enum.chatDecision),
    args: ChatDecisionParams.describe('Parameters for relaying user decision'),
  }),
]);

// Create TypeScript types based on the schemas
export type ChatCommandType = z.infer<typeof ChatCommandTypeEnum>;
export type ChatCommand = z.infer<typeof ChatCommandSchema>;
export type FixCompilationErrorParams = z.infer<typeof FixCompilationErrorParams>;
export type RollbackParams = z.infer<typeof RollbackParams>;
export type ChatDecisionParams = z.infer<typeof ChatDecisionParams>;
